import React from 'react';
import { useTranslation } from 'react-i18next';

import { useEnrolUser } from '@justgiving/experiments';
import loadable from '@loadable/component';

import {
  FundraisingPageFragment,
  PageStatus,
} from '../../../types/__generated__';
import config from '../../client/config';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { Banner } from '../../components/Banner/Banner';
import { DonateToYourselfModal } from '../../components/DonateToYourselfModal/DonateToYourselfModal';
import DonationSummary, {
  DonationField,
} from '../../components/DonationSummary/DonationSummary';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import { FitbitManager } from '../../components/FitnessManager/FitbitManager';
import { StravaManager } from '../../components/FitnessManager/StravaManager';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import FundraisingHelmet from '../../components/Helmet/FundraisingHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import { PageOwnerAvatar } from '../../components/PageOwnerAvatar/PageOwnerAvatar';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import Reason from '../../components/Reason/Reason';
import { ShareModal } from '../../components/ShareModal/ShareModal';
import { FundraisingShareProvider } from '../../components/SocialButtons/FundraisingShareProvider';
import SocialButtonsList from '../../components/SocialButtons/SocialButtonsList';
import Story from '../../components/Story/Story';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { FirstToDonateTotals } from '../../components/Totals/FirstToDonateTotals';
import { Totals } from '../../components/Totals/Totals';
import VideoStoryCheckList from '../../components/VideoStoryCheckList/VideoStoryCheckList';
import { Experiments } from '../../constants/experiments';
import { MODAL } from '../../context';
import {
  buildQuery,
  useApp,
  useFlags,
  usePageMeta,
  usePageOwner,
  usePageQuery,
} from '../../hooks';
import { useOpenDonateToYourselfModal } from '../../hooks/fundraising/useOpenDonateToYourselfModal';
import { useInView } from '../../hooks/useinView';
import { useScript } from '../../hooks/useScript';
import { useShouldDisplayFirstToDonateTotals } from '../../hooks/useShouldDisplayFirstToDonateTotals';
import Fragment from './FundraisingPageFragment.gql';

import styles from './FundraisingPage.scss';
import { useDecision } from '@optimizely/react-sdk';
import { TotalsProgressBar } from '../../components/ActivityTotals/TotalsProgressBar/TotalsProgressBar';
import AboveTheFoldCtaPageOwnerStory from '../../components/AboveTheFoldCtaPageOwnerStory/AboveTheFoldCtaPageOwnerStory';
import ShowOn from '../../components/ShowOn/ShowOn';
import { Share } from '../../components/SocialButtons/Share';

const Prompt = loadable(() => import('../../components/Prompt/Prompt'));
const QRModal = loadable(() => import('../../components/QRModal/QRModal'));
const Editbar = loadable(() => import('../../components/Editbar/Editbar'));
const TimelinePageSection = loadable(() =>
  import('../../components/Timeline/TimelinePageSection/TimelinePageSection'),
);
const TeamAndCompany = loadable(() =>
  import('../../components/TeamAndCompany/TeamAndCompany'),
);
const QualarooLoader = loadable(
  () => import('../../components/QualarooLoader/QualarooLoader'),
  { resolveComponent: module => module.QualarooLoader },
);
const StickySummary = loadable(() =>
  import('../../components/StickySummary/StickySummary'),
);
const EncourageInactivityModal = loadable(() =>
  import('../../components/EncourageInactivityModal/EncourageInactivityModal'),
);

const FundraisingPageQuery = buildQuery(Fragment);

export const FundraisingPage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });

  const [
    { variationKey: AboveTheFoldCtaProgressBarFrpAbExperiment },
  ] = useDecision(
    Experiments.AboveTheFoldCtaProgressBarFrpFeature
      .AboveTheFoldCtaProgressBarFrpAbExperiment.experimentKey,
  );

  const isEnrolledInAboveTheFoldCtaExperiment =
    AboveTheFoldCtaProgressBarFrpAbExperiment ===
    Experiments.AboveTheFoldCtaProgressBarFrpFeature
      .AboveTheFoldCtaProgressBarFrpAbExperiment.variations
      .AboveFoldCtaProgressBarMobile;

  const [{ variationKey: ShareButtonBorderExperiment }] = useDecision(
    Experiments.ShareButtonBorderFeature.ShareButtonBorderExperiment
      .experimentKey,
  );

  const isEnrolledInShareButtonBorderExperiment =
    ShareButtonBorderExperiment ===
    Experiments.ShareButtonBorderFeature.ShareButtonBorderExperiment.variations
      .BorderedShareButton;

  const { inViewRef, inView, rootIsBelowTarget } = useInView();
  const { data } = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = usePageMeta();
  const { useOTGVideoGenerator, enableFitbit } = useFlags();
  useOpenDonateToYourselfModal({
    totalDonationAmount: data?.donationSummary?.totalAmount?.value,
  });

  useEnrolUser(Experiments.donate_now_track_click, false);

  const shouldDisplayFirstToDonateTotals = useShouldDisplayFirstToDonateTotals();

  const name = data?.owner?.name ?? '';
  const eventGivingGroupId = data?.eventGivingGroupId;
  const totalRelatedCompanies = data?.relationships?.companies.totalCount ?? 0;

  const displayTeamAndCompany =
    totalRelatedCompanies > 0 ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  const adminLink = `${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`;
  const fundraisingPageUrl = `${config.baseUri}/${pageShortName}`;

  const { hideModal, openModal } = useApp();
  const { isPageOwner } = usePageOwner();

  const totals = () => {
    return (
      <>
        <ShowOn
          mobileViewport={isEnrolledInAboveTheFoldCtaExperiment}
          desktopViewport={false}
        >
          <div className={styles.progressBarWrapper}>
            <TotalsProgressBar />
          </div>
        </ShowOn>

        <ShowOn
          mobileViewport={!isEnrolledInAboveTheFoldCtaExperiment}
          desktopViewport={true}
        >
          <TotaliserWrapper />
          {shouldDisplayFirstToDonateTotals ? (
            <FirstToDonateTotals />
          ) : (
            <Totals />
          )}
        </ShowOn>
      </>
    );
  };

  const pageOwnerAvatarStory = () => {
    return (
      <>
        <ShowOn
          mobileViewport={isEnrolledInAboveTheFoldCtaExperiment}
          desktopViewport={false}
        >
          <AboveTheFoldCtaPageOwnerStory />
        </ShowOn>

        <ShowOn
          mobileViewport={!isEnrolledInAboveTheFoldCtaExperiment}
          desktopViewport={true}
        >
          <PageOwnerAvatar />
        </ShowOn>
      </>
    );
  };

  return (
    <FundraisingShareProvider>
      <Page>
        {openModal === MODAL.DONATE_TO_YOURSELF && <DonateToYourselfModal />}
        {openModal === MODAL.SHARE && (
          <ShareModal
            heading={
              isPageOwner
                ? t('shareModal.heading.pageOwner')
                : t('shareModal.heading', {
                    pageOwner: data?.owner?.name,
                  })
            }
            description={t('shareModal.description')}
            onClose={hideModal}
          />
        )}
        <IsQRCode>
          <QRModal ownerName={name} />
        </IsQRCode>
        <PageAlert />
        <Banner />
        {eventGivingGroupId && (
          <IsPageOwner>
            <Prompt
              entityId={eventGivingGroupId.toString()}
              url={fundraisingPageUrl}
            />
          </IsPageOwner>
        )}
        <Editbar adminLink={adminLink} />
        <FundraisingHelmet />
        <DefaultLayout
          slots={{
            featureImage: <FeatureImage enableLightbox />,
            ownerAvatar: pageOwnerAvatarStory(),
            titles: <PageOwnerTitle />,
            totals: totals(),
            callsToAction: (
              <div ref={inViewRef} className={styles.ctaWrapper}>
                <FundraisingCTA />
              </div>
            ),
            titlesSupplementary: (
              <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
                <EventDetails />
                <Reason />
                <TeamDetails />
              </div>
            ),
            causeMini: <AboutCauseMini />,
            main: (
              <>
                <PageSection
                  title={t('story.title')}
                  className="jg-pages-fundraising__story"
                  hasBorder={true}
                >
                  <Story />
                  <h2 className="jg-h3 jg-text--center">
                    {t('story.shareTitle', 'Share this story')}
                  </h2>
                  <SocialButtonsList />
                </PageSection>
                <TimelinePageSection />
              </>
            ),
            aside: (
              <>
                {useOTGVideoGenerator && (
                  <IsPageOwner>
                    <PageSection>
                      <VideoStoryCheckList />
                    </PageSection>
                  </IsPageOwner>
                )}
                <PageSection>
                  <StravaManager />
                </PageSection>
                {enableFitbit && (
                  <PageSection>
                    <FitbitManager />
                  </PageSection>
                )}
                <SupportersList
                  noSupportersMessage={t(
                    'fundraising.noSupportersMessage',
                    `Become {{name}}'s first supporter by making a donation`,

                    { replace: { name } },
                  )}
                  displayDonateButton
                  donateButtonExperiments={[Experiments.donate_now_track_click]}
                />
              </>
            ),
            teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
            contentFooterPrimary: <AboutCampaignAndCharitySection />,
            contentFooterSecondary: (
              <DonationSummary
                fieldsToShow={[
                  DonationField.OnlineAmount,
                  DonationField.OfflineAmount,
                  DonationField.TextDonations,
                ]}
              />
            ),
          }}
        />
        {!inView && rootIsBelowTarget && (
          <StickySummary
            donateButtonExperiments={[Experiments.donate_now_track_click]}
            shareComponent={
              <Share
                buttonClassName={
                  isEnrolledInShareButtonBorderExperiment
                    ? 'jg-btn--ghost sticky-ghost-share-button'
                    : undefined
                }
              />
            }
            shareWrapperClassName={
              isEnrolledInShareButtonBorderExperiment
                ? 'jg-space-pbn'
                : undefined
            }
          />
        )}
        <EncourageInactivityModal />
        <QualarooLoader />
      </Page>
    </FundraisingShareProvider>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
